import { FormEvent, useEffect, useState } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { SaveMedium } from '@zoopla-eng/zds-components/icons';
import { PictogramPricesSmall } from '@zoopla-eng/zds-components/pictograms';
import { RadioGroup } from '@zoopla-eng/zds-components/radio-group';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

import {
    getRelationshipCopy,
    mapRelationship,
    relationshipsList,
} from '@zoopla/map-relationship';
import { TrackedPropertyRelationship } from '../../graphql.generated';
import { useSaveTrackedPropertyMutation } from '../../queries/saveTrackedPropertyMutation.generated';
import {
    buttonContainerStyle,
    consentContainerStyle,
    errorStyle,
} from './ConsentAndRelationshipModal.styles.css';
import { PICTOGRAMS_REMOTE_SPRITE_URL } from '@zoopla/assets';
import { setupLogger } from '@zoopla/logger';
import { ColouredCard } from '@zoopla-eng/zds-components/coloured-card';

type ConsentAndRelationshipModalProps = {
    isOpen: boolean;
    close: () => void;
    onSave: (relationship?: TrackedPropertyRelationship) => void;
    address: string;
    uprn: string;
};

const consentData = {
    text: "You'll get a monthly valuation email, plus updates about this property and the local area.",
    id: '6',
};

export const ConsentAndRelationshipModal = ({
    isOpen,
    close,
    onSave,
    address,
    uprn,
}: ConsentAndRelationshipModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [propertyRelationship, setPropertyRelationship] =
        useState<TrackedPropertyRelationship>();
    const [saveTrackedProperty] = useSaveTrackedPropertyMutation();

    const saveTrackingAndConsent = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setIsLoading(true);
        setErrorText('');
        try {
            await saveTrackedProperty({
                variables: {
                    trackedProperty: {
                        uprn,
                        relationship: propertyRelationship,
                        consentText: consentData.text,
                        consentId: consentData.id,
                    },
                },
            });
            onSave(propertyRelationship);
        } catch (err) {
            const logger = setupLogger({
                page: 'agent-matcher',
                custodian: 'home',
            });

            logger.error({ err }, 'House prices useTrackProperty error: ');
            setErrorText('Something went wrong. Please try again');
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setErrorText('');
            setIsLoading(false);
            setPropertyRelationship(undefined);
        }
    }, [isOpen]);

    return (
        <ControlledDialog
            isOpen={isOpen}
            onClose={close}
            title="Set your property relationship"
            label={`Set your property relationship`}
            mobileVariant="bottomSheet"
        >
            <ColouredCard backgroundColor="graphicBackgroundAccentLight">
                <Box display="flex" columnGap="S400" alignItems="center">
                    <PictogramPricesSmall
                        backgroundColor="graphicBackgroundAccentLight"
                        remoteSpriteUrl={PICTOGRAMS_REMOTE_SPRITE_URL}
                    />
                    <h2 className={fontGroups.ladle} data-cs-mask>
                        {address}
                    </h2>
                </Box>
            </ColouredCard>
            <form
                name="property-relationship-group"
                onSubmit={saveTrackingAndConsent}
            >
                <Box py="M100">
                    <RadioGroup<TrackedPropertyRelationship | undefined>
                        id="property-relationship"
                        label="Property relationship"
                        hideLabel
                        items={relationshipsList.map((relationship) => ({
                            id: mapRelationship(
                                relationship
                            )?.toLocaleLowerCase(),
                            label: getRelationshipCopy(relationship),
                            value: relationship,
                        }))}
                        onValueChange={setPropertyRelationship}
                        value={propertyRelationship}
                    />
                </Box>
                <Divider direction="horizontal" />
                <Box pt="S300">
                    <div aria-live="polite">
                        {errorText && (
                            <div
                                className={`${errorStyle} ${fontGroups.fork}`}
                                data-testid="tracking-error-consent-modal"
                            >
                                {errorText}
                            </div>
                        )}
                    </div>
                </Box>
                <div className={consentContainerStyle}>
                    <p
                        id="relationship-modal-consent"
                        className={fontGroups.fork}
                    >
                        {consentData.text}
                    </p>
                    <div className={buttonContainerStyle}>
                        <ButtonLeft
                            category="primary"
                            Icon={SaveMedium}
                            loading={isLoading}
                            loadingText="Tracking"
                            aria-describedby="relationship-modal-consent"
                            fw
                        >
                            Track property
                        </ButtonLeft>
                    </div>
                </div>
            </form>
        </ControlledDialog>
    );
};
